import { Link } from "react-router-dom";
import LegalPage, { Mail, OPERATOR, type LegalSection } from "../components/LegalPage";

const SECTIONS: LegalSection[] = [
  {
    heading: "Information we collect",
    body: (
      <>
        <p>We collect only what we need to run Set &amp; Ledger:</p>
        <ul>
          <li>
            <strong>Account information:</strong> your name, email address and password. We store your password only
            as a one-way hash, never in readable form. We also record when you sign in.
          </li>
          <li>
            <strong>Mailbox connections:</strong> when you connect Gmail or Google Workspace, we receive read-only
            access to your mail (the <code>gmail.readonly</code> permission) and your email address. For Outlook or
            Microsoft 365 we receive read-only mail access (<code>Mail.Read</code>) and your basic profile. For other
            mailboxes we store the IMAP server, username and app password you enter. These access tokens and passwords
            are encrypted at rest.
          </li>
          <li>
            <strong>Email you import:</strong> for the period you choose, we download your messages: sender,
            recipients, subject, date, body and attachments. We store them with your account so we can tell which ones
            are receipts, bills or payments, and show you the original next to what we extracted. Messages that don't
            look financial are marked as skipped and aren't sent to the AI, but they stay stored with your account
            until you delete them or your account.
          </li>
          <li>
            <strong>Files you upload:</strong> receipts and invoices you upload yourself.
          </li>
          <li>
            <strong>Records we extract:</strong> vendor and client names, amounts, taxes, currencies, dates, invoice
            numbers and payment card details limited to the card brand and last four digits.
          </li>
          <li>
            <strong>QuickBooks Online connection:</strong> if you connect QuickBooks, the access tokens (encrypted),
            your company ID, the default accounts you choose and a log of what we synced.
          </li>
          <li>
            <strong>Your settings and activity in the app:</strong> review status, vendor renames and rules, saved
            reports and similar choices.
          </li>
          <li>
            <strong>Technical information:</strong> IP address and browser details in our server and security logs, and
            a record of each AI request (model used, size and the structured result).
          </li>
        </ul>
        <p>
          We don't use advertising or analytics cookies. Your sign-in session is kept in your browser's local storage.
        </p>
      </>
    ),
  },
  {
    heading: "How we use it",
    body: (
      <>
        <ul>
          <li>
            To provide the service: importing mail, extracting records with AI, spotting duplicates, the review and
            reporting tools, and syncing to QuickBooks when you ask.
          </li>
          <li>To keep the service secure, investigate problems you report and keep it working reliably.</li>
          <li>To contact you about your account, security and changes to the service.</li>
        </ul>
        <p>
          We do not sell your personal information, use it for advertising, or use your emails, files or financial
          records to train AI models.
        </p>
      </>
    ),
  },
  {
    heading: "How the AI processing works",
    body: (
      <>
        <p>
          Emails that look financial, their attachments, files you upload, and short summaries of extracted records
          (for vendor matching and Bulk Review checks) are sent to AI models through OpenRouter, Inc., which passes
          each request to a model provider (currently Google's Gemini models). They process the request to return a
          result to us. We send only what each task needs. Their handling of the data is also governed by their own
          terms and privacy policies.
        </p>
        <p>AI results can be wrong, which is why the app lets you review and correct every record.</p>
      </>
    ),
  },
  {
    heading: "Google user data",
    body: (
      <>
        <p>
          Set &amp; Ledger's use and transfer of information received from Google APIs to any other app will adhere to
          the{" "}
          <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noreferrer">
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements. In particular:
        </p>
        <ul>
          <li>
            We use Gmail data (your messages, attachments and email address) only to provide the features you see in
            Set &amp; Ledger: finding and extracting your receipts, bills and payments.
          </li>
          <li>
            We share it only with the service providers needed to run those features (the AI processing and hosting
            described here), when you send records to QuickBooks, or when the law requires it. We never sell it or use
            it for advertising.
          </li>
          <li>
            No person reads your Gmail data except with your permission for specific messages (for example, when you
            report a parsing problem), when needed for security, or to comply with the law.
          </li>
          <li>We don't use Gmail data to develop, improve or train generalized AI or machine-learning models.</li>
        </ul>
        <p>
          You can disconnect a mailbox on the Connections page at any time, and revoke our access in your{" "}
          <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer">
            Google account settings
          </a>
          .
        </p>
      </>
    ),
  },
  {
    heading: "Who we share it with",
    body: (
      <>
        <p>We share information only with the service providers that help us run Set &amp; Ledger:</p>
        <ul>
          <li>
            <strong>OVHcloud:</strong> hosts our servers and database in Canada.
          </li>
          <li>
            <strong>Cloudflare:</strong> provides DNS, encryption in transit and network security; your traffic to the
            site passes through it.
          </li>
          <li>
            <strong>OpenRouter and AI model providers:</strong> process the content described above.
          </li>
          <li>
            <strong>Google, Microsoft or your mail provider:</strong> when you connect a mailbox, so we can fetch your
            mail.
          </li>
          <li>
            <strong>Intuit:</strong> when you connect QuickBooks Online, we send the records you sync to your company.
          </li>
          <li>
            <strong>Google Fonts:</strong> the website loads its fonts from Google, which receives your IP address.
          </li>
        </ul>
        <p>
          We may also disclose information when the law requires it, to protect the rights, safety or security of our
          users or others, or as part of a merger or sale of the business (we'd tell you first).
        </p>
      </>
    ),
  },
  {
    heading: "Where your data is stored",
    body: (
      <p>
        Your account data, imported emails, files and records are stored on servers in Canada. Some of the providers
        above (such as OpenRouter, AI model providers, Cloudflare, Google, Microsoft and Intuit) may process data in the
        United States or other countries, where it is subject to the laws of those countries.
      </p>
    ),
  },
  {
    heading: "How long we keep it, and deleting it",
    body: (
      <>
        <ul>
          <li>We keep your information while your account is active.</li>
          <li>
            You can disconnect a mailbox at any time and delete the receipts imported from it. You can also delete
            individual receipts.
          </li>
          <li>
            To delete your account and everything in it (imported emails, files, records and connections), email{" "}
            <Mail /> from the address on your account. We'll delete it within 30 days. Records you already sent to
            QuickBooks stay in your QuickBooks company; manage those there.
          </li>
          <li>Server logs are kept only as long as needed for security and troubleshooting.</li>
        </ul>
      </>
    ),
  },
  {
    heading: "Security",
    body: (
      <p>
        Connections to Set &amp; Ledger are encrypted in transit. Mailbox and QuickBooks access tokens and IMAP
        passwords are encrypted at rest, account passwords are hashed, and mail access is read-only. Access to
        production systems is limited to authorized administrators. No system is perfectly secure; if a breach affects
        your information, we'll notify you and the authorities as the law requires.
      </p>
    ),
  },
  {
    heading: "Your rights",
    body: (
      <p>
        Under Canada's Personal Information Protection and Electronic Documents Act (PIPEDA) and other laws that apply
        to you, you can ask to access or correct your personal information, or withdraw your consent (for example, by
        disconnecting a mailbox or deleting your account; without that access we can't provide the service). Email{" "}
        <Mail /> and we'll respond within 30 days. If you're not satisfied with our answer, you can contact the{" "}
        <a href="https://www.priv.gc.ca" target="_blank" rel="noreferrer">
          Office of the Privacy Commissioner of Canada
        </a>
        .
      </p>
    ),
  },
  {
    heading: "Children",
    body: <p>Set &amp; Ledger is a business tool for people 18 and older. It isn't directed at children.</p>,
  },
  {
    heading: "Changes to this policy",
    body: (
      <p>
        If we change this policy, we'll post the new version here with a new effective date. For significant changes,
        we'll also let you know by email or in the app before they take effect.
      </p>
    ),
  },
  {
    heading: "Contact us",
    body: (
      <p>
        {OPERATOR}, Ontario, Canada. Privacy questions and requests: <Mail />. See also our{" "}
        <Link to="/terms">Terms of Service</Link>.
      </p>
    ),
  },
];

export default function Privacy() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Privacy Policy"
      intro={
        <p>
          Set &amp; Ledger (www.setandledger.com) is operated by {OPERATOR} ("we", "us"). This policy explains what
          personal information we collect when you use Set &amp; Ledger, how we use and share it, and the choices you
          have.
        </p>
      }
      sections={SECTIONS}
    />
  );
}
